import { Notification } from '@carbon/icons-react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { formatDateTime, relativeTime } from '@/shared/format/date';
import { cx } from '@/shared/lib/cx';
import { Button, EmptyState, IconButton, Popover, SkeletonText, useTimeZone } from '@/ui';
import { QueryErrorState } from '../api/QueryErrorState';
import { safeInternalLink, type AppNotification } from '../api/notificationsApi';
import { useRealtimeEvent, useServices, useSession } from '../AppServicesContext';
import { sectionForPath } from '../registry';
import s from '../layouts/Layout.module.css';

/** Центр уведомлений с настройкой каналов (ПОРТ-60) — раздел features/notifications. */
const NOTIFICATION_CENTER_PATH = '/notifications';

export const notificationsQueryKey = (clinicId: string | null) => ['notifications', clinicId ?? 'none', 'list'] as const;
export const unreadCountQueryKey = (clinicId: string | null) => ['notifications', clinicId ?? 'none', 'unread'] as const;

function useRelativeTimeLabel() {
  const { t } = useTranslation('shell');
  const timeZone = useTimeZone();
  return (value: string) => {
    const relative = relativeTime(value);
    if (!relative) return formatDateTime(value, { timeZone });
    if (relative.unit === 'now') return t('time.now');
    return t(`time.${relative.unit}Ago`, { count: relative.count });
  };
}

/** Центр уведомлений в приложении (ПОРТ-60): непрочитанные, новые — в реальном времени по SSE. */
export function NotificationCenter() {
  const { t } = useTranslation('shell');
  const { notifications } = useServices();
  const { state } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const timeLabel = useRelativeTimeLabel();
  const queryKey = notificationsQueryKey(state.clinicId);

  const [opened, setOpened] = useState(false);
  const hasCenter = sectionForPath(NOTIFICATION_CENTER_PATH) !== null;
  const live = state.status === 'authenticated' && !state.locked;
  /* Счётчик нужен всегда (значок на кнопке), список — только когда панель открыта. */
  const unreadQuery = useQuery({
    queryKey: unreadCountQueryKey(state.clinicId),
    queryFn: ({ signal }) => notifications.unreadCount(signal),
    enabled: live,
    staleTime: 60_000,
    meta: { silent: true },
  });
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam, signal }) => notifications.list({ cursor: pageParam, limit: 20 }, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextCursor,
    enabled: live && opened,
    staleTime: 60_000,
  });

  useRealtimeEvent('notifications.created', () => {
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  });

  const markRead = useMutation({
    mutationFn: (id: string) => notifications.markRead(id),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
    meta: { silent: true },
  });
  const markAll = useMutation({
    mutationFn: () => notifications.markAllRead(),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const items: AppNotification[] = query.data?.pages.flatMap((page) => page.items) ?? [];
  const unread = unreadQuery.data ?? 0;

  const open = (item: AppNotification, close: () => void) => {
    if (!item.readAt) markRead.mutate(item.id);
    const link = safeInternalLink(item.link);
    if (link) {
      close();
      navigate(link);
    }
  };

  return (
    <Popover
      title={t('notifications.title')}
      flush
      width={400}
      onOpenChange={setOpened}
      trigger={(props) => (
        <IconButton
          {...props}
          label={t('notifications.open')}
          icon={Notification}
          badge={unread}
          badgeStyle="dot"
          badgeLabel={t('notifications.unread', { count: unread })}
        />
      )}
    >
      {(close) => (
        <div>
          {query.isPending ? (
            <div className={s.popoverPad} aria-busy="true">
              <SkeletonText lines={4} />
            </div>
          ) : query.isError ? (
            <QueryErrorState error={query.error} onRetry={() => void query.refetch()} compact headingLevel={3} />
          ) : items.length === 0 ? (
            <EmptyState icon={Notification} compact headingLevel={3} title={t('notifications.emptyTitle')} description={t('notifications.emptyDescription')} />
          ) : (
            <>
              <ul className={s.notificationList} aria-label={t('notifications.listLabel')}>
                {items.map((item) => (
                  <li key={item.id}>
                    <button type="button" className={s.notificationItem} onClick={() => open(item, close)}>
                      <span className={cx(s.unreadDot, item.readAt && s.readDot)} aria-hidden="true" />
                      <span className={s.notificationBody}>
                        <span className={s.notificationTitle}>
                          {item.readAt ? null : <span className="visually-hidden">{t('notifications.unreadMark')}: </span>}
                          {item.title}
                        </span>
                        {item.body ? <span className={s.notificationText}>{item.body}</span> : null}
                        <time className={s.notificationTime} dateTime={item.createdAt}>
                          {timeLabel(item.createdAt)}
                        </time>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
          {items.length > 0 || hasCenter ? (
            <div className={s.notificationFooter}>
              {items.length > 0 ? (
                <Button variant="ghost" size="sm" onClick={() => markAll.mutate()} loading={markAll.isPending} disabled={unread === 0}>
                  {t('notifications.markAllRead')}
                </Button>
              ) : null}
              {query.hasNextPage ? (
                <Button variant="ghost" size="sm" onClick={() => void query.fetchNextPage()} loading={query.isFetchingNextPage}>
                  {t('notifications.more')}
                </Button>
              ) : null}
              {hasCenter ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    close();
                    navigate(NOTIFICATION_CENTER_PATH);
                  }}
                >
                  {t('notifications.openCenter')}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </Popover>
  );
}
